/**
 * Aktuelle Kursposition (Modul/Kapitel) — geteilt zwischen Kurs-Screen, Heute
 * („Fortsetzen, wo du warst") und Coach-Kontext. Letzte Position wird wie im Web
 * persistiert (Schlüssel `ki-kurs-letzter`).
 */
import { signal } from "@preact/signals";
import { activeProfileId } from "./session";

/** Profil-namensraumig wie im Web: `ki-kurs-letzter::<profil-id>`. */
const lastKey = () => `ki-kurs-letzter::${activeProfileId()}`;

export interface CoursePosition {
  courseId: string;
  moduleId: string;
  blockId: string;
  /** Abschnitts-Index im Kapitel (Task F, Web: ?u=N). */
  subIndex?: number;
  /** Web-kompatible URL (wie app.js sie schreibt). */
  url?: string;
}

export const moduleId = signal<string | null>(null);
export const blockId = signal<string | null>(null);

/**
 * Sprung-Brücke für Kapitel-Deep-Links aus dem KI-Coach.
 *
 * `selectChapter` lebt lokal in `screens/Course.tsx` und ist nicht exportierbar.
 * Damit ein Coach-Klick auch im Same-Route-Fall (man ist schon auf /kurs, das
 * Coach-Sheet ist offen) springen kann, setzt der Handler hier ein Signal; der
 * Kurs-Screen hat einen Effect, der darauf reagiert und `selectChapter(m, b)`
 * ruft. So entsteht KEIN doppelter History-Eintrag (selectChapter pusht selbst).
 */
export interface JumpRequest {
  m: string;
  b: string;
  /** Optionaler Abschnitts-Slug (`&u=<slug>` aus Chat-Sprunglinks bis Unterkapitel). */
  u?: string;
}
export const jumpRequest = signal<JumpRequest | null>(null);

/** Sprung-Wunsch ablegen (vom Coach-Klickhandler aufgerufen). */
export function requestJump(m: string, b: string, u?: string): void {
  jumpRequest.value = { m, b, u };
}

/**
 * Streng angekertes Schema der Kurs-Sprung-Links (1:1 zur Web-App,
 * `public/assets/chatbot.js:30`): `modul.html?m=<m>[&u=<abschnitt-slug>]#<kapitel-id>`.
 * Großbuchstaben sind erlaubt (IDs/Slugs sind Überschriften-Slugs). Wird sowohl beim
 * Parsen (`coach/format.tsx`) als auch beim Klick (`coach/CoachPanel.tsx`) verwendet,
 * damit den gerenderten Attributen nicht blind vertraut wird.
 */
const JUMP_TARGET_RE = /^modul\.html\?m=([A-Za-z0-9_-]+)(?:&u=([A-Za-z0-9_-]+))?#([A-Za-z0-9_-]+)$/;

/** Ein Link-Ziel gegen das Kurs-Schema prüfen; Treffer → {m, b, u?}, sonst null. */
export function parseJumpTarget(target: string): JumpRequest | null {
  const m = JUMP_TARGET_RE.exec(target);
  return m ? { m: m[1], b: m[3], u: m[2] || undefined } : null;
}

export function setPosition(courseId: string, m: string, b: string, subIndex = 0): void {
  moduleId.value = m;
  blockId.value = b;
  try {
    const uq = subIndex > 0 ? `&u=${subIndex}` : "";
    const url = `modul.html?course=${encodeURIComponent(courseId)}&m=${encodeURIComponent(m)}${uq}#${b}`;
    localStorage.setItem(lastKey(), JSON.stringify({ courseId, moduleId: m, blockId: b, subIndex, url }));
  } catch {
    /* ignore */
  }
}

export function lastPosition(): CoursePosition | null {
  try {
    const raw = localStorage.getItem(lastKey());
    return raw ? (JSON.parse(raw) as CoursePosition) : null;
  } catch {
    return null;
  }
}

export function clearLastPosition(): void {
  try {
    localStorage.removeItem(lastKey());
  } catch {
    /* ignore */
  }
  moduleId.value = null;
  blockId.value = null;
}
